// Keep missing measurements separate from genuine zeroes in forms and map summaries.
export function finiteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
export function optionalNumber(value) {
    if (!value.trim())
        return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}
export function formatNumber(value, digits = 1) {
    return finiteNumber(value) ? value.toFixed(digits) : '—';
}
export function sampleYear(date) {
    // A sampling date belongs to its stated year, not the browser's timezone.
    return /^\d{4}-\d{2}-\d{2}/.test(date) ? Number(date.substring(0, 4)) : NaN;
}
export function latestSeasonSamples(rows, season) {
    const selected = rows.filter((row) => sampleYear(row.agro_year) === season);
    const latestDate = selected.map((row) => row.agro_year.substring(0, 10)).sort().pop();
    return latestDate ? selected.filter((row) => row.agro_year.substring(0, 10) === latestDate) : [];
}
export function latestSeasonCalculation(rows, fieldId, season, accepts = () => true) {
    var _a;
    return (_a = rows.filter((row) => row.fertilizer_field_id === fieldId && row.fertilizer_season === season && accepts(row))
        .sort((a, b) => {
        var _a, _b;
        return (Date.parse((_a = b.fertilizer_date_created) !== null && _a !== void 0 ? _a : '') || 0) - (Date.parse((_b = a.fertilizer_date_created) !== null && _b !== void 0 ? _b : '') || 0)
            || b.id - a.id;
    })[0]) !== null && _a !== void 0 ? _a : null;
}
// A nitrogen-only calculation must not shadow previously saved soil, phosphorus or potassium maps.
export function fertilizerCalculationSupports(row, nutrient, variant = 'ndvi') {
    var _a;
    const source = row.fertilizer_source_metadata;
    if ((source === null || source === void 0 ? void 0 : source.calculation_scope) === 'soil_nitrogen')
        return nutrient === 'nitrogen' && variant !== 'ndvi';
    const nitrogenOnly = (source === null || source === void 0 ? void 0 : source.calculation_scope) === 'nitrogen' || ((_a = source === null || source === void 0 ? void 0 : source.nitrogen_prescription) === null || _a === void 0 ? void 0 : _a.method) === 'fabbri_ndvil';
    return !nitrogenOnly || (nutrient === 'nitrogen' && variant === 'ndvi');
}
export function latestNutrientCalculation(rows, fieldId, season, nutrient, variant = 'ndvi') {
    // A later P/K recalculation still contains legacy N bands; it must not undo a Fabbri nitrogen plan.
    if (nutrient === 'nitrogen' && variant === 'ndvi') {
        const prescription = latestSeasonCalculation(rows, fieldId, season, (row) => { var _a, _b; return ((_b = (_a = row.fertilizer_source_metadata) === null || _a === void 0 ? void 0 : _a.nitrogen_prescription) === null || _b === void 0 ? void 0 : _b.method) === 'fabbri_ndvil'; });
        if (prescription)
            return prescription;
    }
    return latestSeasonCalculation(rows, fieldId, season, (row) => fertilizerCalculationSupports(row, nutrient, variant));
}
export function bandValueUnit(band) {
    return band >= 1 && band <= 3 ? 'мг/кг' : 'кг/га';
}
export function isCalendarDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().substring(0, 10) === value;
}
export function taskMapsAvailable(context, season) {
    return Boolean((context === null || context === void 0 ? void 0 : context.can_plan) && season === context.active_season
        && isCalendarDate(context.today) && Number(context.today.substring(0, 4)) === season);
}
export function assessmentDateError(assessmentDate, context, season) {
    if (!context)
        return 'Не удалось определить дату и доступность расчёта. Обновите страницу.';
    if (!taskMapsAvailable(context, season))
        return context.reason || 'Карты-задания доступны только для активного сезона.';
    if (!isCalendarDate(assessmentDate))
        return 'Укажите дату оценки.';
    if (Number(assessmentDate.substring(0, 4)) !== season)
        return 'Дата оценки должна относиться к выбранному сезону.';
    if (assessmentDate > context.today)
        return 'Дата оценки не может быть позже сегодняшнего дня.';
    return null;
}
export function planningDateError(assessmentDate, plannedDate, context, season) {
    const assessmentError = assessmentDateError(assessmentDate, context, season);
    if (assessmentError)
        return assessmentError;
    if (!isCalendarDate(plannedDate))
        return 'Укажите планируемую дату внесения.';
    if (Number(plannedDate.substring(0, 4)) !== season)
        return 'Дата внесения должна относиться к выбранному сезону.';
    if (plannedDate < context.today)
        return 'Планируемая дата внесения не может быть в прошлом.';
    if (plannedDate < assessmentDate)
        return 'Дата внесения не может быть раньше даты оценки.';
    return null;
}
export function formatCalendarDate(value) {
    if (!value || !isCalendarDate(value))
        return 'не указана';
    return `${value.substring(8, 10)}.${value.substring(5, 7)}.${value.substring(0, 4)}`;
}
export function sourcePreviewUsable(preview, allowAging) {
    var _a, _b;
    const source = preview === null || preview === void 0 ? void 0 : preview.source;
    if (!(preview === null || preview === void 0 ? void 0 : preview.available) || !source || !/^[0-9a-f]{64}$/.test((_a = preview.source_selection_key) !== null && _a !== void 0 ? _a : '')
        || source.selection_key !== preview.source_selection_key || !isCalendarDate((_b = source.acquisition_date) !== null && _b !== void 0 ? _b : ''))
        return false;
    const ages = [source.age_at_assessment_days, source.age_at_application_days];
    if (!ages.every((age) => finiteNumber(age) && age >= 0 && age <= 14))
        return false;
    const aging = ages.some((age) => age > 7);
    return source.freshness === (aging ? 'aging' : 'fresh') && (!aging || allowAging);
}
